import { X } from 'lucide-react';
import { useState } from 'react';

import { useToast } from '@/components/ToastProvider';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { useRemoveTeamUsageCredits } from '@/hooks/useTeams';
import { useRemoveUsageCredits } from '@/hooks/useUsers';
import { usdTextToCents } from '@/lib/usd';

type UsageCreditsWallet =
  | { kind: 'user'; userId: number }
  | { kind: 'team'; teamId: string; userId?: number };

export default function RemoveUsageCreditsModal({
  isOpen,
  onClose,
  wallet,
  purchasedBalance,
  rateCentsPer1000,
}: {
  isOpen: boolean;
  onClose: () => void;
  wallet: UsageCreditsWallet;
  purchasedBalance: number | undefined;
  rateCentsPer1000: number | undefined;
}) {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const removeUserCredits = useRemoveUsageCredits();
  const removeTeamCredits = useRemoveTeamUsageCredits();
  const toast = useToast();

  if (!isOpen) return null;

  const isPending = removeUserCredits.isPending || removeTeamCredits.isPending;
  const dollars = parseFloat(amount);
  const credits =
    rateCentsPer1000 !== undefined && Number.isFinite(dollars) && dollars > 0
      ? Math.floor((usdTextToCents(String(dollars)) * 1000) / rateCentsPer1000)
      : null;
  const exceedsBalance =
    credits !== null &&
    purchasedBalance !== undefined &&
    credits > purchasedBalance;
  const owner = wallet.kind === 'team' ? "the team's" : "the user's";

  const handleClose = () => {
    setAmount('');
    setReason('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!Number.isFinite(dollars) || dollars <= 0)
      return toast.error('Invalid amount', 'Enter a positive dollar value');
    if (
      !window.confirm(
        `Remove ${credits?.toLocaleString() ?? ''} credits ($${amount}) from ${owner} purchased balance?`
      )
    )
      return;
    try {
      const res =
        wallet.kind === 'team'
          ? await removeTeamCredits.mutateAsync({
              teamId: wallet.teamId,
              userId: wallet.userId,
              amount_usd: dollars,
              reason: reason || undefined,
            })
          : await removeUserCredits.mutateAsync({
              userId: wallet.userId,
              amountUsd: dollars,
              reason: reason || undefined,
            });
      toast.success(
        'Usage credits removed',
        `Purchased balance: ${res.purchased_balance} (total ${res.balance})`
      );
      handleClose();
    } catch (e) {
      toast.error(
        'Failed to remove usage credits',
        e instanceof Error ? e.message : 'Unknown error'
      );
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-3 sm:items-center"
      onClick={handleClose}
    >
      <Card
        className="my-auto w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Remove usage credits</CardTitle>
              <CardDescription>
                Take purchased credits back from {owner} purchased balance, such
                as after refunding a credit pack in Stripe (ledger source:
                admin).
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm">
            Current purchased balance:{' '}
            <span className="font-semibold">
              {purchasedBalance?.toLocaleString() ?? '…'} credits
            </span>
          </p>
          <div>
            <label className="text-sm font-medium mb-1 block">
              Dollar value
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="5.00"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
            {credits !== null && (
              <p
                className={`text-xs mt-1 ${exceedsBalance ? 'text-destructive' : 'text-muted-foreground'}`}
              >
                = {credits.toLocaleString()} credits
                {exceedsBalance && ', more than the purchased balance'}
              </p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Reason</label>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Refunded credit pack"
              className="w-full px-3 py-2 border rounded-md bg-background"
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              variant="destructive"
              onClick={handleSubmit}
              disabled={isPending || !amount || exceedsBalance}
              className="flex-1"
            >
              {isPending ? 'Removing...' : 'Remove usage credits'}
            </Button>
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
